// Check your email (spec C7.1): six boxes for the emailed code, Remember this device, Resend.
// Markup from design/kit/components/CodeInput, Checkbox and the "Sign in and system states" board.
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { Api } from '../auth/api';
import {
  backspace,
  codeOf,
  emptyBoxes,
  isComplete,
  moveFocus,
  resendLabel,
  typeInto,
  type BoxChange,
} from '../auth/code-boxes';
import { copy, rateLimitText, type SignInNotice } from '../auth/copy';
import { safeNext } from '../auth/next';
import { AmberLine, Brand, CardTitle, ErrorLine, SignInLayout } from '../auth/SignInLayout';
import { InfoIcon } from '../shell/icons';
import type { SignInState } from './SignInPage';

// The server refuses a resend for a while after each code: 30 seconds unless the Admin changed it in
// Settings, Integrations. Sign in passes the real wait; this is only for a page opened without it.
export const RESEND_SECONDS = 30;

type CodeError = { text: string; dead: boolean } | null;

export function CodePage({ api }: { api: Api }) {
  const state = useLocation().state as { email?: string; resendIn?: number } | null;
  const [params] = useSearchParams();
  // Opened without coming from Sign in (a typed address, an old tab): start at Sign in.
  if (!state?.email) return <Navigate to="/sign-in" replace />;
  return (
    <CodeCard
      api={api}
      email={state.email}
      next={safeNext(params.get('next'))}
      firstWait={typeof state.resendIn === 'number' ? state.resendIn : RESEND_SECONDS}
    />
  );
}

function CodeCard({
  api,
  email,
  next,
  firstWait,
}: {
  api: Api;
  email: string;
  next: string;
  firstWait: number;
}) {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState(emptyBoxes);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<CodeError>(null);
  const [wrongTries, setWrongTries] = useState(0);
  const [resendLeft, setResendLeft] = useState(firstWait);
  const [resent, setResent] = useState(false);
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const focusBox = (i: number) => {
    const el = refs.current[i];
    el?.focus();
    el?.select();
  };

  useEffect(() => {
    focusBox(0);
  }, []);

  useEffect(() => {
    if (resendLeft <= 0) return;
    const t = setTimeout(() => setResendLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendLeft]);

  const backToSignIn = (notice: SignInNotice) =>
    navigate('/sign-in', { replace: true, state: { email, notice } satisfies SignInState });

  const apply = ({ boxes: b, focus }: BoxChange) => {
    setBoxes(b);
    // Typing again after a wrong code clears the red boxes; a dead code needs a new one first.
    if (error && !error.dead) setError(null);
    focusBox(focus);
  };

  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      apply(backspace(boxes, i));
    } else if (
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Home' ||
      e.key === 'End'
    ) {
      e.preventDefault();
      focusBox(moveFocus(i, e.key));
    }
  };

  const complete = isComplete(boxes);
  const canVerify = complete && !busy && !error?.dead;

  async function verify(e: FormEvent) {
    e.preventDefault();
    if (!canVerify) return;
    setBusy(true);
    const res = await api.verifyCode(codeOf(boxes), remember);
    if (res.ok) {
      navigate(next, { replace: true });
      return;
    }
    setBusy(false);
    if (res.error === 'wrong') {
      setWrongTries((n) => n + 1);
      setError({ text: copy.wrongCode, dead: false });
      focusBox(0);
    } else if (res.error === 'expired') {
      setError({ text: copy.codeExpired, dead: true });
      setResendLeft(0);
    } else if (res.error === 'too_many_tries') {
      setWrongTries((n) => n + 1);
      setError({ text: copy.tooManyTries, dead: true });
      setResendLeft(0);
    } else if (res.error === 'no_attempt') {
      backToSignIn('timed_out');
    } else if (res.error === 'disabled') {
      backToSignIn('disabled');
    } else if (res.error === 'rate_limited') {
      setError({ text: rateLimitText(res), dead: false });
    } else {
      setError({ text: copy.somethingWrong, dead: false });
    }
  }

  async function resend() {
    if (resendLeft > 0) return;
    const res = await api.resendCode();
    if (res.ok) {
      setResendLeft(res.body.resendIn);
      setResent(true);
      setError(null);
      setBoxes(emptyBoxes());
      focusBox(0);
      return;
    }
    if (res.error === 'resend_wait' && typeof res.body.resendIn === 'number') {
      setResendLeft(res.body.resendIn);
    } else if (res.error === 'no_attempt') {
      backToSignIn('timed_out');
    } else if (res.error === 'too_many_codes') {
      backToSignIn('too_many_codes');
    } else if (res.error === 'rate_limited') {
      setError({ text: rateLimitText(res), dead: !!error?.dead });
    } else {
      setError({ text: copy.somethingWrong, dead: !!error?.dead });
    }
  }

  // Red boxes for a wrong code; the other errors are about the code, not what was typed.
  const red = error?.text === copy.wrongCode;

  return (
    <SignInLayout>
      <form className="lcard" aria-labelledby="code-title" noValidate onSubmit={verify}>
        <Brand />
        <CardTitle id="code-title" title={copy.checkEmail}>
          {copy.sentTo(email)}
        </CardTitle>
        <div className={red ? 'otp err' : 'otp'} role="group" aria-label="6-digit code">
          {boxes.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              className="input"
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={6}
              aria-label={`Digit ${i + 1}`}
              aria-invalid={red || undefined}
              value={digit}
              onFocus={(e) => e.target.select()}
              onChange={(e) => apply(typeInto(boxes, i, e.target.value))}
              onPaste={(e) => {
                e.preventDefault();
                apply(typeInto(boxes, i, e.clipboardData.getData('text')));
              }}
              onKeyDown={onKeyDown(i)}
            />
          ))}
        </div>
        {error && <ErrorLine text={error.text} center />}
        <label className="rmb">
          <input
            type="checkbox"
            className="cbx"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span>{copy.remember}</span>
        </label>
        <button type="submit" className="btn btn-primary wide" disabled={!canVerify}>
          {copy.verify}
        </button>
        <div className="col g4" style={{ alignItems: 'center' }}>
          <button type="button" className="linkbtn" disabled={resendLeft > 0} onClick={resend}>
            {resendLabel(resendLeft)}
          </button>
          {resent && <span className="t12 muted">{copy.resent}</span>}
        </div>
        {wrongTries > 0 && <AmberLine icon={<InfoIcon />} text={copy.help} />}
      </form>
    </SignInLayout>
  );
}
